import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const numberFmt = new Intl.NumberFormat('en-US');
const compactFmt = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/**
 * Large counts (≥100k) render compactly — `100k`, `1.2M`, `10M` — so a tile
 * never overflows with a long string of digits; smaller numbers stay fully
 * written out. The exact value is exposed on hover by the caller.
 */
const formatStatNumber = (n: number): string =>
  Math.abs(n) >= 100_000 ? compactFmt.format(n).replace('K', 'k') : numberFmt.format(n);

export type Stat = {
  /** Stable key — also used as the React key. */
  key: string;
  label: string;
  /** Numbers are locale-formatted; pass a string to opt out. */
  value: number | string | null | undefined;
  /** Secondary line, e.g. a share of the total. */
  hint?: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** Tailwind classes for the icon tile. Defaults to neutral grey. */
  tone?: string;
};

type StatsGridProps = {
  stats: Stat[];
  isLoading?: boolean;
  /** Rendered in place of the grid when the stats request failed. */
  error?: string | null;
  className?: string;
  /** When set, tiles become buttons that call this with the stat key. */
  onStatClick?: (key: string) => void;
  /** Keys whose tiles render as selected / pressed. */
  activeKeys?: string[];
};

const StatTile: React.FC<{
  stat: Stat;
  active?: boolean;
  onClick?: () => void;
}> = ({ stat, active, onClick }) => {
  const { label, value, hint, icon: Icon, tone } = stat;
  const interactive = Boolean(onClick);

  const body = (
    <>
      {Icon && (
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
            tone ?? 'bg-gray-50 text-gray-600',
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
      )}
      <div className="min-w-0 flex-1 text-left">
        <p className="truncate text-[11px] font-medium uppercase tracking-wide text-gray-400">
          {label}
        </p>
        <p
          className="truncate text-lg font-semibold tabular-nums text-gray-900"
          title={typeof value === 'number' ? numberFmt.format(value) : undefined}
        >
          {value === null || value === undefined
            ? '—'
            : typeof value === 'number'
              ? formatStatNumber(value)
              : value}
        </p>
        {hint && <p className="truncate text-[10px] text-gray-400">{hint}</p>}
      </div>
    </>
  );

  const tileClass = cn(
    'flex min-w-0 items-center gap-3 rounded-lg border bg-white px-3.5 py-3 transition',
    active ? 'border-teal-400 ring-2 ring-teal-100' : 'border-gray-200',
    interactive &&
      'cursor-pointer hover:border-gray-300 hover:bg-gray-50/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-200',
  );

  if (interactive) {
    return (
      <button type="button" onClick={onClick} aria-pressed={active} className={tileClass}>
        {body}
      </button>
    );
  }

  return <div className={tileClass}>{body}</div>;
};

const StatsGrid: React.FC<StatsGridProps> = ({
  stats,
  isLoading,
  error,
  className,
  onStatClick,
  activeKeys,
}) => {
  if (error) {
    return (
      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        Couldn’t load summary counts — {error}
      </div>
    );
  }

  const activeSet = activeKeys ? new Set(activeKeys) : null;

  return (
    <div
      className={cn(
        'mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-3 2xl:grid-cols-6',
        className,
      )}
    >
      {isLoading
        ? Array.from({ length: stats.length || 6 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3.5 py-3"
            >
              <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <Skeleton className="h-2.5 w-16" />
                <Skeleton className="h-4 w-12" />
              </div>
            </div>
          ))
        : stats.map((stat) => (
            <StatTile
              key={stat.key}
              stat={stat}
              active={activeSet?.has(stat.key)}
              onClick={onStatClick ? () => onStatClick(stat.key) : undefined}
            />
          ))}
    </div>
  );
};

export default StatsGrid;
